import { Role } from 'auth';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { MouseEvent } from 'react';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import CommunicationRecipientsModal from '@/modules/issuer-dashboard/components/modals/CommunicationRecipientsModal.tsx';
import { IssuerAddAnnouncementModal } from '@/modules/issuer-dashboard/components/modals/IssuerAddAnnouncementModal.tsx';
import IssuerCorrespondenceItemModal from '@/modules/issuer-dashboard/components/modals/IssuerCorrespondenceItemModal.tsx';
import { useGetRegister } from '@/modules/issuer-dashboard/hooks/useGetRegister.tsx';
import { useIssuerCommunicationsListQuery } from '@/modules/issuer-dashboard/queries/communications.ts';
import {
  Badge,
  Button,
  FilterItem,
  InfoCard,
  ModalProviderContext,
  PageLayout,
  SearchField,
  SelectOption,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  TooltipNew,
} from 'ui';
import { ArrowShortLeftIcon, CopyIcon } from 'ui/icons';
import { copyTextToClipBoard } from 'ui/utils';
import {
  CommunicationType,
  CorrespondenceCommunicationActivityItem,
  CorrespondenceCommunicationStatus,
  CorrespondenceHolder,
  GetCommunicationCorrespondenceListV1SrCommunicationCorrespondenceTrackingGetParams,
} from 'api';

const mapCommunicationStatusToBadge: Record<CorrespondenceCommunicationStatus, ReactNode> = {
  [CorrespondenceCommunicationStatus.Incompleted]: (
    <Badge containerClass="w-fit bg-[#0369A1] !justify-start text-white text-xs">In Progress</Badge>
  ),
  [CorrespondenceCommunicationStatus.Received]: (
    <Badge containerClass="w-fit bg-[#525252] !justify-start text-white text-xs">Received</Badge>
  ),
  [CorrespondenceCommunicationStatus.Completed]: (
    <Badge containerClass="w-fit bg-[#16A34A] !justify-start text-white text-xs">Completed</Badge>
  ),
};

const typeOptions: SelectOption<CommunicationType>[] = [
  { value: CommunicationType.Communication, label: 'Communication' },
  { value: CommunicationType.External, label: 'External' },
];

const headItems: TableHeadColumn[] = [
  { title: 'Subject', className: 'min-w-[250px] !pr-3' },
  { title: 'Sender', className: '!px-3' },
  { title: 'Date & Time', className: 'min-w-[150px] !px-3' },
  { title: 'Type', className: '!px-3' },
  { title: 'Status', className: '!px-3' },
  { title: 'Recipients', className: '!px-3 w-40' },
];

type Filters = Omit<
  GetCommunicationCorrespondenceListV1SrCommunicationCorrespondenceTrackingGetParams,
  'page_size' | 'page'
>;

const IssuerCommunications = () => {
  const { openModal } = useContext(ModalProviderContext);
  const [filters, setFilters] = useState<Filters>({});
  const register = useGetRegister();

  const { data, isLoading, fetchNextPage, hasNextPage } = useIssuerCommunicationsListQuery({
    page: 1,
    page_size: 15,
    ...filters,
  });

  const handleOpenAnnouncementModal = useCallback(() => {
    openModal(IssuerAddAnnouncementModal);
  }, [openModal]);

  const handleOpenCorrespondenceItemModal = useCallback(
    (item: CorrespondenceCommunicationActivityItem) => {
      openModal(IssuerCorrespondenceItemModal, { item });
    },
    [openModal],
  );

  const handleOpenCommunicationRecipientsModal = useCallback(
    (holders: CorrespondenceHolder[], e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      openModal(CommunicationRecipientsModal, { holders });
    },
    [openModal],
  );

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    return (data?.pages || []).flatMap(({ data }) =>
      data.map(item => {
        const { subject, created_at, status, type, recipients, sender_email, sender_name } = item;
        return {
          onClick: () => handleOpenCorrespondenceItemModal(item),
          cells: [
            { cell: <div className="truncate max-w-[40vw]">{subject}</div>, className: '!pr-3' },
            {
              cell: (
                <div className="max-w-[150px]">
                  {type === CommunicationType.External && (
                    <>
                      {sender_name && <div className="!text-sm truncate">{sender_name}</div>}
                      {sender_email && <div className="text-gray-600 truncate">{sender_email}</div>}
                    </>
                  )}
                  {type === CommunicationType.Communication && 'Compli One'}
                </div>
              ),
              className: '!px-3',
            },
            { cell: dayjs(created_at).format('DD-MM-YYYY, HH:mm'), className: '!px-3' },
            { cell: <div className="capitalize">{type}</div>, className: '!px-3' },
            { cell: status && mapCommunicationStatusToBadge[status], className: '!px-3' },
            {
              cell: (
                <TooltipNew
                  text="Message sent to the register’s email address, not to holders."
                  disabled={type !== CommunicationType.External}
                >
                  <div className="text-gray-500">
                    {type === CommunicationType.Communication &&
                      (recipients?.length ? (
                        <div
                          className="flex gap-1 hover:underline"
                          onClick={handleOpenCommunicationRecipientsModal.bind(null, recipients)}
                        >
                          <div className="truncate max-w-32">{recipients[0].recipient_email}</div>
                          <img
                            src={ArrowShortLeftIcon}
                            alt="open"
                            className="-rotate-90"
                            width={6}
                          />
                        </div>
                      ) : (
                        '---'
                      ))}
                    {type === CommunicationType.External && 'Sent to register'}
                  </div>
                </TooltipNew>
              ),
              className: '!px-3',
            },
          ],
        };
      }),
    );
  }, [data?.pages, handleOpenCommunicationRecipientsModal, handleOpenCorrespondenceItemModal]);

  const handleChangeSearchFilter = useCallback((text: string) => {
    setFilters(filters => (text ? { ...filters, text } : omit(filters, 'text')));
  }, []);

  const handleChangeTypeFilter = useCallback((type: CommunicationType | null) => {
    setFilters(filters => (type ? { ...filters, type } : omit(filters, 'type')));
  }, []);

  const handleChangeSenderFilter = useCallback((sender: string) => {
    setFilters(filters => (sender ? { ...filters, sender } : omit(filters, 'sender')));
  }, []);

  const handleChangeRecipientsFilter = useCallback((recipient: string) => {
    setFilters(filters => (recipient ? { ...filters, recipient } : omit(filters, 'recipient')));
  }, []);
  return (
    <PageLayout title="Communication" contentClassName="!p-0">
      <div className="px-3 sm:px-6 py-5">
        <h2 className="text-gray-500">Choose how you'd like to send a message</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <InfoCard title="Send via app" classes={{ content: 'space-y-3 pt-3' }}>
            <p className="text-sm">
              Compose and send your message directly from within the platform. Ideal for quick and
              secure communication with built-in tracking.
            </p>
            <Button className="w-fit" onClick={handleOpenAnnouncementModal}>
              New email
            </Button>
          </InfoCard>
          <InfoCard title="Copy Email Address" classes={{ content: 'space-y-3 pt-3' }}>
            <p
              className="flex gap-1 items-center cursor-pointer text-sm min-w-[220px]"
              onClick={copyTextToClipBoard.bind(null, register?.correspondence_email || '', {})}
            >
              <span className="truncate">{register?.correspondence_email}</span>
              <img src={CopyIcon} alt="copy" width={18} />
            </p>
            <p className="text-sm">
              Compose an email using your external tool (Outlook, Gmail, etc) and add this email in
              CC to track your email correspondence with investors through Compli One.
            </p>
          </InfoCard>
        </div>
        <div className="grid gap-3 mt-6 grid-cols-[repeat(auto-fill,_minmax(calc(25%_-_9px),_300px))]">
          <SearchField
            allowClear
            className="max-w-[440px]"
            placeholder="Search by email text"
            onChange={handleChangeSearchFilter}
          />
          <FilterItem
            type="select"
            label="Type"
            classes={{ label: 'min-w-[85px]', element: 'w-full' }}
            value={filters.type}
            options={typeOptions}
            onChange={handleChangeTypeFilter}
          />
          <FilterItem
            type="custom"
            label="Sender"
            classes={{ label: 'min-w-[85px]', element: 'w-full' }}
          >
            <SearchField
              plain
              allowClear
              placeholder="Name or email"
              onChange={handleChangeSenderFilter}
            />
          </FilterItem>
          <FilterItem type="custom" label="Recipient" classes={{ element: 'w-full' }}>
            <SearchField
              plain
              allowClear
              placeholder="Name or email"
              onChange={handleChangeRecipientsFilter}
            />
          </FilterItem>
        </div>
      </div>
      <Table
        blockIdInfinite="scrollable"
        isSearchUsed={!isEmpty(filters)}
        isLoading={isLoading}
        fetchNextPageInfinite={fetchNextPage}
        hasNextPageInfinite={hasNextPage}
        withRounded={false}
        withBorder={false}
        withBorderX={false}
        className="mt-3 [&_th]:text-xs [&_td:not(.absolute)]:text-xs [&_td.absolute]:!top-[-60px]"
        items={bodyItems}
        headItems={headItems}
      />

      {/*<EmptyListState*/}
      {/*  title="No emails yet"*/}
      {/*  description="Communications feature allows you to send emails to all investors of your registry"*/}
      {/*  isLoading={isAnnouncementListLoading}*/}
      {/*  isSearchUsed={false}*/}
      {/*  itemsCount={announcements?.pages.flatMap(({ data }) => data).length ?? 0}*/}
      {/*/>*/}
    </PageLayout>
  );
};

export default withPrivateAccess(IssuerCommunications, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
